import React, { useState, useEffect } from 'react';
import './App.css';
import logo from './logo.svg';
import { firebaseConfig } from './firebaseConfig';
import { useAuth } from './auth/AuthProvider';

// ... (imports des vues)
import DashboardView from './components/DashboardView';
import BillingView from './components/BillingView';
import InvoiceEditor from './components/InvoiceEditor';
import PatientDetails from './components/PatientDetails';
import Onboarding from './components/Onboarding';
import PatientsView from './components/PatientsView';
import ServiceSelectionView from './components/ServiceSelectionView';
import AboutView from './components/AboutView';
import FeaturesView from './components/FeaturesView';
import ContactView from './components/ContactView';
import PricingView from './components/PricingView';
import PharmacyView from './components/PharmacyView';
import CashRegisterView from './components/CashRegisterView';
import FloatingAssistant from './components/FloatingAssistant';
import DeviceConnectionsView from './components/DeviceConnectionsView';
import AttendanceView from './components/AttendanceView';
import AdminReportsView from './components/AdminReportsView';
import { flushSyncQueue, queueSync } from './utils/offlineSync';
import { useTranslation } from 'react-i18next';
import { createInsuranceCode, createPatientCode, createReceiptCode } from './utils/identifiers';

function App() {
    const { t, i18n } = useTranslation();
    const SESSION_DURATION_MS = 12 * 60 * 60 * 1000;
    // ... (tous les états restent identiques)
    const [appConfig, setAppConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [patients, setPatients] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [staff, setStaff] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [currentView, setCurrentView] = useState('features');
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [pendingView, setPendingView] = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const [isOnline, setIsOnline] = useState(() => navigator.onLine);
    const [installPrompt, setInstallPrompt] = useState(null);
    const [cashTransactions, setCashTransactions] = useState([]);
    const [pharmacyStock, setPharmacyStock] = useState([]);
    const [stockTransactions, setStockTransactions] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [labOrders, setLabOrders] = useState([]);
    const [pharmacyOrders, setPharmacyOrders] = useState([]);

    const getDefaultAuthenticatedView = (config, service) => {
        if (!config) return 'patients';
        if (config.centerType === 'personal') return 'patients';
        return service ? 'dashboard' : 'service_selection';
    };

    const { user: firebaseUser, loading: firebaseLoading } = useAuth();

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        const handleInstallPrompt = (event) => {
            event.preventDefault();
            setInstallPrompt(event);
        };
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('beforeinstallprompt', handleInstallPrompt);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
        };
    }, []);

    const installApp = async () => {
        if (!installPrompt) return;
        installPrompt.prompt();
        await installPrompt.userChoice;
        setInstallPrompt(null);
    };

    useEffect(() => {
        if (firebaseLoading) return;
        if (firebaseUser) {
            const userObj = { id: firebaseUser.uid, name: firebaseUser.displayName || firebaseUser.email };
            localStorage.setItem('currentUser', JSON.stringify(userObj));
            localStorage.setItem('sessionExpiresAt', String(Date.now() + SESSION_DURATION_MS));
            setCurrentUser(userObj);
        }
    }, [firebaseUser, firebaseLoading]);

    useEffect(() => {
        const loadState = async () => {
            try {
                const loadedUser = JSON.parse(localStorage.getItem('currentUser'));
                const sessionExpiresAt = Number(localStorage.getItem('sessionExpiresAt') || 0);
                const loadedConfig = JSON.parse(localStorage.getItem('appConfig'));
                const loadedService = localStorage.getItem('selectedService');

                if (loadedUser && sessionExpiresAt > Date.now()) {
                    setCurrentUser(loadedUser);
                    setAppConfig(loadedConfig);
                    setSelectedService(loadedService || null);
                    setCurrentView(getDefaultAuthenticatedView(loadedConfig, loadedService));

                    if (isFirebaseConfigured()) {
                        await Promise.all([
                            fetchPatientsFromDB(),
                            fetchInvoicesFromDB(),
                            fetchStaffFromDB(),
                            fetchSchedulesFromDB()
                        ]);
                    } else {
                        setPatients(JSON.parse(localStorage.getItem('patients')) || []);
                        setInvoices(JSON.parse(localStorage.getItem('invoices')) || []);
                        setSchedules(JSON.parse(localStorage.getItem('schedules')) || []);
                        setStaff(JSON.parse(localStorage.getItem('staff')) || []);
                        setCashTransactions(JSON.parse(localStorage.getItem('cashTransactions')) || []);
                        setPharmacyStock(JSON.parse(localStorage.getItem('pharmacyStock')) || []);
                        setStockTransactions(JSON.parse(localStorage.getItem('stockTransactions')) || []);
                    }
                } else {
                    localStorage.removeItem('currentUser');
                    localStorage.removeItem('sessionExpiresAt');
                    setCurrentView('features');
                }

                setCashTransactions(JSON.parse(localStorage.getItem('cashTransactions')) || []);
                setPharmacyStock(JSON.parse(localStorage.getItem('pharmacyStock')) || []);
                setStockTransactions(JSON.parse(localStorage.getItem('stockTransactions')) || []);
                setAttendance(JSON.parse(localStorage.getItem('attendance')) || []);
                setLabOrders(JSON.parse(localStorage.getItem('labOrders')) || []);
                setPharmacyOrders(JSON.parse(localStorage.getItem('pharmacyOrders')) || []);
            } catch (error) {
                console.error("Erreur lors du chargement des données depuis localStorage", error);
                setCurrentView('features');
            } finally {
                setIsLoading(false);
            }
        };

        loadState();
    }, []);

    useEffect(() => {
        if (!isLoading && currentUser) {
            localStorage.setItem('patients', JSON.stringify(patients));
            localStorage.setItem('invoices', JSON.stringify(invoices));
            localStorage.setItem('staff', JSON.stringify(staff));
            localStorage.setItem('schedules', JSON.stringify(schedules));
            localStorage.setItem('cashTransactions', JSON.stringify(cashTransactions));
            localStorage.setItem('pharmacyStock', JSON.stringify(pharmacyStock));
            localStorage.setItem('stockTransactions', JSON.stringify(stockTransactions));
            localStorage.setItem('attendance', JSON.stringify(attendance));
            localStorage.setItem('labOrders', JSON.stringify(labOrders));
            localStorage.setItem('pharmacyOrders', JSON.stringify(pharmacyOrders));
            if (selectedService) {
                localStorage.setItem('selectedService', selectedService);
            }
        }
    }, [patients, invoices, staff, schedules, cashTransactions, pharmacyStock, stockTransactions, attendance, labOrders, pharmacyOrders, selectedService, isLoading, currentUser]);

    const handleLogin = async (config) => {
        const user = {
            id: Date.now(),
            name: config.centerName || 'Administrateur',
            role: 'Administrateur',
            service: 'Administration'
        };

        const storedPatients = JSON.parse(localStorage.getItem('patients')) || [];
        const storedInvoices = JSON.parse(localStorage.getItem('invoices')) || [];
        const storedStaff = JSON.parse(localStorage.getItem('staff')) || [];
        const storedSchedules = JSON.parse(localStorage.getItem('schedules')) || [];

        const samplePatients = storedPatients.length > 0 ? storedPatients : [
            {
                name: 'Dupont',
                prenom: 'Jean',
                sexe: 'M',
                dateNaissance: new Date('1984-03-12').toISOString().slice(0, 10),
                age: 42,
                phone: '+33 6 12 34 56 78',
                adresse: '1 Rue de la Paix, Paris',
                nomMere: 'Marie Dupont',
                nomJeuneFille: '',
                etatCivil: 'marie',
                consultations: [
                    {
                        id: 111,
                        date: new Date().toISOString().slice(0, 10),
                        practitioner: user.name,
                        service: 'Consultation générale',
                        signesVitaux: { ta: '12/8', temp: '37.5', fc: '78', fr: '18', poids: '73', taille: '178' },
                        motifConsultation: ['Grippe', 'Fièvre', '', '', ''],
                        impressionClinique: 'Grippe légère',
                        examensComplementaires: 'Test rapide antigénique si besoin',
                        conduiteATenir: 'Repos, hydratation, paracétamol si fièvre'
                    }
                ]
            }
        ];

        const sampleStaff = storedStaff.length > 0 ? storedStaff : [
            { id: '1', name: 'Dr. Martin', role: 'Médecin' }
        ];

        const today = new Date().toISOString().slice(0, 10);
        const sampleSchedules = storedSchedules.length > 0 ? storedSchedules : [
            { id: '1', staffId: '1', staffName: 'Dr. Martin', staffRole: 'Médecin', shift: '08h-12h', date: today }
        ];

        localStorage.setItem('currentUser', JSON.stringify(user));
        localStorage.setItem('sessionExpiresAt', String(Date.now() + SESSION_DURATION_MS));
        localStorage.setItem('appConfig', JSON.stringify(config));
        localStorage.setItem('selectedService', selectedService || '');
        setCurrentUser(user);
        setAppConfig(config);
        setSelectedService(localStorage.getItem('selectedService') || null);

        let patientsToUse = storedPatients.length > 0 ? storedPatients : samplePatients;
        let invoicesToUse = storedInvoices;
        let staffToUse = storedStaff.length > 0 ? storedStaff : sampleStaff;
        let schedulesToUse = storedSchedules.length > 0 ? storedSchedules : sampleSchedules;

        if (isFirebaseConfigured()) {
            const [remotePatients, remoteInvoices, remoteStaff, remoteSchedules] = await Promise.all([
                fetchPatientsFromDB(),
                fetchInvoicesFromDB(),
                fetchStaffFromDB(),
                fetchSchedulesFromDB()
            ]);

            patientsToUse = remotePatients.length > 0 ? remotePatients : [];
            invoicesToUse = remoteInvoices;
            staffToUse = remoteStaff.length > 0 ? remoteStaff : staffToUse;
            schedulesToUse = remoteSchedules.length > 0 ? remoteSchedules : schedulesToUse;

            if (remotePatients.length === 0) {
                for (const sample of samplePatients) {
                    await createPatientInDB(sample);
                }
                patientsToUse = await fetchPatientsFromDB();
            }
        }

        localStorage.setItem('patients', JSON.stringify(patientsToUse));
        localStorage.setItem('invoices', JSON.stringify(invoicesToUse));
        localStorage.setItem('staff', JSON.stringify(staffToUse));
        localStorage.setItem('schedules', JSON.stringify(schedulesToUse));

        setPatients(patientsToUse);
        setInvoices(invoicesToUse);
        setStaff(staffToUse);
        setSchedules(schedulesToUse);

        const previousSelectedService = localStorage.getItem('selectedService');
        const serviceToUse = previousSelectedService || null;
        setSelectedService(serviceToUse);
        const nextView = pendingView || getDefaultAuthenticatedView(config, serviceToUse);
        const destination = (config.centerType !== 'personal' && nextView === 'dashboard' && !serviceToUse) ? 'service_selection' : nextView;
        setPendingView(null);
        navigateToWithHistory(destination);
    };

    const handleLogout = () => {
        localStorage.clear();
        setCurrentUser(null);
        setAppConfig(null);
        setPatients([]);
        setInvoices([]);
        setSchedules([]);
        setStaff([]);
        setCashTransactions([]);
        setPharmacyStock([]);
        setStockTransactions([]);
        setAttendance([]);
        setLabOrders([]);
        setPharmacyOrders([]);
        setCurrentView('features');
    };

    const handleUpdatePatient = (updatedPatient) => {
        const newPatients = patients.map(p => p.id === updatedPatient.id ? updatedPatient : p);
        setPatients(newPatients);
        setSelectedPatient(updatedPatient);
    };

    // Simple Firestore integration wrappers (fall back to localStorage when Firebase not configured)
    const isFirebaseConfigured = () => {
        return firebaseConfig && firebaseConfig.apiKey && !firebaseConfig.apiKey.includes('REPLACE');
    };

    const fetchPatientsFromDB = async () => {
        if (!isFirebaseConfigured()) return [];
        try {
            const svc = await import('./utils/patientService');
            const remote = await svc.fetchPatients();
            setPatients(remote);
            localStorage.setItem('patients', JSON.stringify(remote));
            return remote;
        } catch (err) {
            console.error('Erreur récupération patients depuis Firestore', err);
            return [];
        }
    };

    const fetchInvoicesFromDB = async () => {
        if (!isFirebaseConfigured()) return [];
        try {
            const svc = await import('./utils/invoiceService');
            const remote = await svc.fetchInvoices();
            setInvoices(remote);
            localStorage.setItem('invoices', JSON.stringify(remote));
            return remote;
        } catch (err) {
            console.error('Erreur récupération factures depuis Firestore', err);
            return [];
        }
    };

    const fetchStaffFromDB = async () => {
        if (!isFirebaseConfigured()) return [];
        try {
            const svc = await import('./utils/staffService');
            const remote = await svc.fetchStaff();
            setStaff(remote);
            localStorage.setItem('staff', JSON.stringify(remote));
            return remote;
        } catch (err) {
            console.error('Erreur récupération du personnel depuis Firestore', err);
            return [];
        }
    };

    const fetchSchedulesFromDB = async () => {
        if (!isFirebaseConfigured()) return [];
        try {
            const svc = await import('./utils/scheduleService');
            const remote = await svc.fetchSchedules();
            setSchedules(remote);
            localStorage.setItem('schedules', JSON.stringify(remote));
            return remote;
        } catch (err) {
            console.error('Erreur récupération des plannings depuis Firestore', err);
            return [];
        }
    };

    const createPatientInDB = async (patient) => {
        const patientWithCodes = {
            ...patient,
            code: patient.code || createPatientCode(),
            insurance: patient.insuranceName ? {
                code: patient.insurance?.code || createInsuranceCode(),
                name: patient.insuranceName,
                memberNumber: patient.insuranceNumber || '',
                status: 'active'
            } : patient.insurance
        };
        if (!isFirebaseConfigured()) {
            const newPatient = { ...patientWithCodes, id: Date.now() };
            const newPatients = [...patients, newPatient];
            setPatients(newPatients);
            localStorage.setItem('patients', JSON.stringify(newPatients));
            return newPatient;
        }
        const localPatient = { ...patientWithCodes, id: patient.id || `offline-${Date.now()}` };
        const newPatients = [...patients, localPatient];
        setPatients(newPatients);
        localStorage.setItem('patients', JSON.stringify(newPatients));
        if (!navigator.onLine) {
            queueSync({ type: 'createPatient', payload: patientWithCodes });
            return localPatient;
        }
        try {
            const svc = await import('./utils/patientService');
            const created = await svc.createPatient(patientWithCodes);
            setPatients(current => current.map(item => item.id === localPatient.id ? created : item));
            return created;
        } catch (error) {
            queueSync({ type: 'createPatient', payload: patientWithCodes });
            return localPatient;
        }
    };

    const updatePatientInDB = async (patient) => {
        if (!isFirebaseConfigured()) {
            const newPatients = patients.map(p => p.id === patient.id ? patient : p);
            setPatients(newPatients);
            localStorage.setItem('patients', JSON.stringify(newPatients));
            return patient;
        }
        const newPatients = patients.map(p => p.id === patient.id ? patient : p);
        setPatients(newPatients);
        localStorage.setItem('patients', JSON.stringify(newPatients));
        if (!navigator.onLine) {
            queueSync({ type: 'updatePatient', payload: patient });
            return patient;
        }
        try {
            const svc = await import('./utils/patientService');
            await svc.updatePatient(patient);
        } catch (error) {
            queueSync({ type: 'updatePatient', payload: patient });
        }
        return patient;
    };

    const deletePatientInDB = async (patientId) => {
        if (!isFirebaseConfigured()) {
            const newPatients = patients.filter(p => p.id !== patientId);
            setPatients(newPatients);
            localStorage.setItem('patients', JSON.stringify(newPatients));
            return;
        }
        const newPatients = patients.filter(p => p.id !== patientId);
        setPatients(newPatients);
        localStorage.setItem('patients', JSON.stringify(newPatients));
        if (!navigator.onLine) {
            queueSync({ type: 'deletePatient', payload: patientId });
            return;
        }
        try {
            const svc = await import('./utils/patientService');
            await svc.deletePatient(patientId);
        } catch (error) {
            queueSync({ type: 'deletePatient', payload: patientId });
        }
    };

    const createInvoiceInDB = async (invoice) => {
        if (!isFirebaseConfigured()) {
            const newInvoice = { ...invoice, id: invoice.id || Date.now() };
            const newInvoices = [...invoices, newInvoice];
            setInvoices(newInvoices);
            localStorage.setItem('invoices', JSON.stringify(newInvoices));
            return newInvoice;
        }
        const localInvoice = { ...invoice, id: invoice.id || `offline-${Date.now()}` };
        const newInvoices = [...invoices, localInvoice];
        setInvoices(newInvoices);
        localStorage.setItem('invoices', JSON.stringify(newInvoices));
        if (!navigator.onLine) {
            queueSync({ type: 'createInvoice', payload: invoice });
            return localInvoice;
        }
        try {
            const svc = await import('./utils/invoiceService');
            const created = await svc.createInvoice(invoice);
            setInvoices(current => current.map(item => item.id === localInvoice.id ? created : item));
            return created;
        } catch (error) {
            queueSync({ type: 'createInvoice', payload: invoice });
            return localInvoice;
        }
    };

    const updateInvoiceInDB = async (invoice) => {
        if (!isFirebaseConfigured()) {
            const newInvoices = invoices.map(inv => inv.id === invoice.id ? invoice : inv);
            setInvoices(newInvoices);
            localStorage.setItem('invoices', JSON.stringify(newInvoices));
            return invoice;
        }
        const newInvoices = invoices.map(inv => inv.id === invoice.id ? invoice : inv);
        setInvoices(newInvoices);
        localStorage.setItem('invoices', JSON.stringify(newInvoices));
        if (!navigator.onLine) {
            queueSync({ type: 'updateInvoice', payload: invoice });
            return invoice;
        }
        try {
            const svc = await import('./utils/invoiceService');
            await svc.updateInvoice(invoice);
        } catch (error) {
            queueSync({ type: 'updateInvoice', payload: invoice });
        }
        return invoice;
    };

    useEffect(() => {
        const syncWhenOnline = async () => {
            await flushSyncQueue({
                createPatient: async (payload) => (await import('./utils/patientService')).createPatient(payload),
                updatePatient: async (payload) => (await import('./utils/patientService')).updatePatient(payload),
                deletePatient: async (payload) => (await import('./utils/patientService')).deletePatient(payload),
                createInvoice: async (payload) => (await import('./utils/invoiceService')).createInvoice(payload),
                updateInvoice: async (payload) => (await import('./utils/invoiceService')).updateInvoice(payload)
            });
        };
        window.addEventListener('online', syncWhenOnline);
        if (navigator.onLine && isFirebaseConfigured()) syncWhenOnline();
        return () => window.removeEventListener('online', syncWhenOnline);
    }, []);

    const recordCashTransaction = async (transaction) => {
        const patient = patients.find(p => String(p.id) === String(transaction.patientId));
        const savedTransaction = {
            ...transaction,
            id: Date.now(),
            receiptCode: transaction.receiptCode || createReceiptCode(),
            amount: Number(transaction.amount),
            date: new Date().toISOString(),
            patientCode: patient?.code || patient?.name || '',
            recordedBy: currentUser?.name || 'Utilisateur'
        };
        setCashTransactions(prev => [savedTransaction, ...prev]);

        if (transaction.invoiceId && transaction.type === 'payment') {
            const invoice = invoices.find(item => String(item.id) === String(transaction.invoiceId));
            if (invoice) {
                await updateInvoiceInDB({
                    ...invoice,
                    status: 'paid',
                    paymentMethod: transaction.paymentMethod,
                    paidAt: savedTransaction.date,
                    amountPaid: Number(transaction.amount)
                });
                setInvoices(prev => prev.map(item => item.id === invoice.id ? {
                    ...item,
                    status: 'paid',
                    paymentMethod: transaction.paymentMethod,
                    paidAt: savedTransaction.date,
                    amountPaid: Number(transaction.amount)
                } : item));
            }
        }
    };

    const recordStockTransaction = (transaction) => {
        const stockItem = pharmacyStock.find(item => item.id === transaction.itemId);
        if (!stockItem) return;
        const quantity = Number(transaction.quantity);
        const delta = transaction.type === 'entrée' ? quantity : -quantity;
        if (stockItem.quantity + delta < 0) {
            alert('Stock insuffisant pour cette sortie.');
            return;
        }
        setPharmacyStock(prev => prev.map(item => item.id === stockItem.id ? { ...item, quantity: item.quantity + delta } : item));
        setStockTransactions(prev => [{ ...transaction, id: Date.now(), date: new Date().toISOString() }, ...prev]);
    };

    const addPharmacyItem = (item) => {
        setPharmacyStock(prev => [...prev, { ...item, id: item.id || `stock-${Date.now()}` }]);
    };

    const recordAttendance = (entry) => {
        setAttendance(prev => [...prev, entry]);
    };

    const createLabOrder = (order) => {
        setLabOrders(prev => [...prev, order]);
    };

    const sendPharmacyOrder = (order) => {
        setPharmacyOrders(prev => [...prev, order]);
    };

    const handleGenerateInvoice = (consultation) => {
        const newInvoice = {
            id: Date.now(),
            patientId: selectedPatient.id,
            date: new Date().toISOString().slice(0, 10),
            consultationId: consultation.id,
            items: [{ description: `Consultation - ${consultation.impressionClinique || 'Diagnostic non spécifié'}`, quantity: 1, price: appConfig?.defaultConsultationPrice || 30 }],
            total: appConfig?.defaultConsultationPrice || 30,
            status: 'unpaid',
            currency: appConfig?.currency || 'USD',
            paymentMethod: 'Especes',
            insurance: ''
        };
        setEditingInvoice(newInvoice);
        setCurrentView('invoice_editor');
    };

    const handleSaveInvoice = async (invoiceToSave) => {
        const index = invoices.findIndex(inv => inv.id === invoiceToSave.id);
        let persistedInvoice = invoiceToSave;
        if (index > -1) {
            persistedInvoice = await updateInvoiceInDB(invoiceToSave);
        } else {
            const invoiceWithoutId = { ...invoiceToSave };
            if (isFirebaseConfigured() && typeof invoiceWithoutId.id !== 'undefined') {
                delete invoiceWithoutId.id;
            }
            persistedInvoice = await createInvoiceInDB(invoiceWithoutId);
        }

        const newInvoices = [...invoices];
        if (index > -1) {
            newInvoices[index] = persistedInvoice;
        } else {
            newInvoices.push(persistedInvoice);
        }

        setInvoices(newInvoices);
        setEditingInvoice(null);
        setCurrentView('billing');
    };

    const handleCancelInvoice = () => {
        setEditingInvoice(null);
        setCurrentView(selectedPatient ? 'patient_details' : 'billing');
    };

    const navigateTo = (view) => setCurrentView(view);
    const navLink = (view) => (event) => {
        event.preventDefault();
        navigateTo(view);
    };

    useEffect(() => {
        const onPop = (e) => {
            const v = (e.state && e.state.view) || window.location.hash.replace('#', '') || 'features';
            setCurrentView(v || 'features');
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    // push state-aware navigateTo so browser back/forward works
    const navigateToWithHistory = (view) => {
        try {
            window.history.pushState({ view }, '', `#${view}`);
        } catch (e) {
            // silent fallback
        }
        setCurrentView(view);
    };

    // ensure navLink uses history-aware navigation
    const navLinkWithHistory = (view) => (event) => {
        if (event && event.preventDefault) event.preventDefault();
        navigateToWithHistory(view);
    };

    const handlePublicNavigate = (view) => {
        if (currentUser) {
            if (view === 'dashboard' && appConfig?.centerType !== 'personal') {
                navigateToWithHistory('service_selection');
            } else {
                navigateToWithHistory(view);
            }
        } else {
            setPendingView(view);
            navigateToWithHistory('login');
        }
    };

    const handleSelectService = (service) => {
        localStorage.setItem('selectedService', service);
        setSelectedService(service);
        navigateToWithHistory('dashboard');
    };

    const renderPublicViews = () => {
        switch (currentView) {
            case 'about': return <AboutView />;
            case 'pricing': return <PricingView />;
            case 'contact': return <ContactView />;
            case 'login': return <Onboarding onSetupComplete={handleLogin} />;
            case 'features':
            default:
                return <FeaturesView onNavigate={handlePublicNavigate} />;
        }
    };

    const renderPrivateViews = () => {
        switch (currentView) {
            case 'patients': return <PatientsView patients={patients} onCreate={createPatientInDB} onUpdate={updatePatientInDB} onDelete={deletePatientInDB} onBack={() => navigateTo(appConfig?.centerType !== 'personal' && !selectedService ? 'service_selection' : 'dashboard')} />;
            case 'service_selection': return <ServiceSelectionView centerType={appConfig?.centerType} onSelectService={handleSelectService} />;
            case 'dashboard':
                if (appConfig?.centerType !== 'personal' && !selectedService) {
                    return <ServiceSelectionView centerType={appConfig?.centerType} onSelectService={handleSelectService} />;
                }
                return <DashboardView patients={patients} schedules={schedules} staff={staff} currentUser={currentUser} centerType={appConfig?.centerType} selectedService={selectedService} onNavigate={() => navigateToWithHistory('service_selection')} onSelectPatient={(p) => { setSelectedPatient(p); navigateTo('patient_details'); }} />;
            case 'patient_details': return <PatientDetails patient={selectedPatient} onBack={() => navigateTo('dashboard')} currentUser={currentUser} onUpdatePatient={handleUpdatePatient} onGenerateInvoice={handleGenerateInvoice} onCreateLabOrder={createLabOrder} onSendPharmacyOrder={sendPharmacyOrder} labOrders={labOrders} invoices={invoices} cashTransactions={cashTransactions} pharmacyStock={pharmacyStock} appConfig={appConfig} />;
            case 'billing': return <BillingView invoices={invoices} patients={patients} appConfig={appConfig} onEditInvoice={(inv) => { setEditingInvoice(inv); navigateTo('invoice_editor'); }} />;
            case 'cash_register': return <CashRegisterView currentUser={currentUser} patients={patients} invoices={invoices} cashTransactions={cashTransactions} onRecordCashTransaction={recordCashTransaction} />;
            case 'pharmacy': return <PharmacyView currentUser={currentUser} pharmacyStock={pharmacyStock} stockTransactions={stockTransactions} onRecordTransaction={recordStockTransaction} onAddItem={addPharmacyItem} labOrders={labOrders} pharmacyOrders={pharmacyOrders} />;
            case 'devices': return <DeviceConnectionsView />;
            case 'attendance': return <AttendanceView staff={staff} attendance={attendance} onRecordAttendance={recordAttendance} />;
            case 'reports': return <AdminReportsView patients={patients} invoices={invoices} cashTransactions={cashTransactions} />;
            case 'invoice_editor': {
                const currentInvoicePatient = editingInvoice ? patients.find(p => p.id === editingInvoice.patientId) : null;
                return currentInvoicePatient ? (
                    <InvoiceEditor invoice={editingInvoice} patient={currentInvoicePatient} onSave={handleSaveInvoice} onCancel={handleCancelInvoice} />
                ) : (
                    <BillingView invoices={invoices} patients={patients} appConfig={appConfig} onEditInvoice={(inv) => { setEditingInvoice(inv); navigateTo('invoice_editor'); }} />
                );
            }
            default: return <DashboardView patients={patients} schedules={schedules} staff={staff} currentUser={currentUser} centerType={appConfig?.centerType} selectedService={selectedService} onNavigate={() => navigateToWithHistory('service_selection')} onSelectPatient={(p) => { setSelectedPatient(p); navigateTo('patient_details'); }} />;
        }
    };

    if (isLoading) return <div className="loading-screen">{t('loading')}</div>;

    return (
        <div className="App">
            {!isOnline && <div className="offline-banner">{t('offline')}</div>}
            <header className="App-header">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ marginRight: '10px' }}>
                        <button type="button" onClick={() => window.history.back()} aria-label="Retour">◀</button>
                        <button type="button" onClick={() => window.history.forward()} aria-label="Avancer">▶</button>
                    </div>
                    {/* 2. Remplacer le titre par le logo et un nouveau titre cliquable */}
                    <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={navLinkWithHistory('features')}>
                        <img src={logo} style={{ height: '40px', marginRight: '15px' }} alt="logo" />
                        <h1 style={{ margin: 0, fontSize: '1.5em' }}>{t('app_name')}</h1>
                    </div>
                </div>
                <nav className="main-nav">
                    {installPrompt && <button type="button" onClick={installApp}>{t('install_app')}</button>}
                    <select aria-label="Language" value={i18n.language} onChange={(event) => i18n.changeLanguage(event.target.value)}>
                        <option value="fr">Français</option>
                        <option value="en">English</option>
                        <option value="pt">Português</option>
                        <option value="es">Español</option>
                        <option value="ht">Kreyòl</option>
                    </select>
                    {currentUser ? (
                        <>
                            <button type="button" onClick={navLinkWithHistory('patients')}>{t('nav_patients')}</button>
                            <button type="button" onClick={navLinkWithHistory(appConfig?.centerType !== 'personal' && !selectedService ? 'service_selection' : 'dashboard')}>{t('nav_dashboard')}</button>
                            <button type="button" onClick={navLinkWithHistory('billing')}>{t('nav_billing')}</button>
                            <button type="button" onClick={navLinkWithHistory('cash_register')}>{t('nav_cash')}</button>
                            <button type="button" onClick={navLinkWithHistory('pharmacy')}>{t('nav_pharmacy')}</button>
                            <button type="button" onClick={navLinkWithHistory('devices')}>Appareils</button>
                            <button type="button" onClick={navLinkWithHistory('attendance')}>Présences</button>
                            <button type="button" onClick={navLinkWithHistory('reports')}>Rapports</button>
                            <button type="button" onClick={(e) => { e.preventDefault(); handleLogout(); }}>{t('nav_logout')} ({currentUser.name})</button>
                        </>
                    ) : (
                        <>
                            <button type="button" onClick={navLinkWithHistory('features')}>{t('nav_features')}</button>
                            <button type="button" onClick={navLinkWithHistory('about')}>{t('nav_about')}</button>
                            <button type="button" onClick={navLinkWithHistory('pricing')}>{t('nav_pricing')}</button>
                            <button type="button" onClick={navLinkWithHistory('contact')}>{t('nav_contact')}</button>
                            <button type="button" onClick={navLinkWithHistory('login')} className="cta-button">{t('nav_access')}</button>
                        </>
                    )}
                </nav>
            </header>
            <main>
                {currentUser ? renderPrivateViews() : renderPublicViews()}
            </main>
            {currentUser && (
                <FloatingAssistant
                    aiName={appConfig?.aiName || 'Assistant'}
                    patients={patients}
                    onSelectPatient={(patient) => { setSelectedPatient(patient); navigateToWithHistory('patient_details'); }}
                />
            )}
        </div>
    );
}

export default App;
